import { Grid, Button, Typography, Popover } from "@mui/material"
import { useState, useEffect } from "react"
import MDSelect from "components/MDSelect"
import MDBox from "components/MDBox"
import MDInput from "components/MDInput"
import { useTreasureHunt } from "components/useTreasureHunt/useTreasureHunt"

import {
  useLazyGetClassSituationSelectionCourseGroupNameQuery,
  useLazyGetClassSituationSelectionScheduleDayQuery,
  useLazyGetClassSituationSelectionLevelNameQuery,
  useLazyGetClassSituationSelectionTeacherNameQuery,
  useLazyGetClassSituationSelectionClassStatusQuery,
  useLazyGetClassSituationSelectionCXOQuery,
} from "shared/redux/endpoints/lxo"

const Filters = ({
  initialFilters,
  onSubmit,
  anchorEl,
  setAnchorEl,
  className,
}) => {
  const { triggerIfNew } = useTreasureHunt("class-situation-filter")
  const [tempFilters, setTempFilters] = useState(initialFilters)

  // Configuration for select components
  const selectConfig = [
    {
      key: "koc",
      trigger: useLazyGetClassSituationSelectionCourseGroupNameQuery,
      placeholder: "Select KOC",
    },
    {
      key: "schedule",
      trigger: useLazyGetClassSituationSelectionScheduleDayQuery,
      placeholder: "Select Schedule Day",
    },
    {
      key: "classLevel",
      trigger: useLazyGetClassSituationSelectionLevelNameQuery,
      placeholder: "Select Level",
    },
    {
      key: "teacher",
      trigger: useLazyGetClassSituationSelectionTeacherNameQuery,
      placeholder: "Select Teacher",
      isSearchable: true,
    },
    // {
    //   key: "status",
    //   trigger: useLazyGetClassSituationSelectionClassStatusQuery,
    //   placeholder: "Select Class Status",
    //   valueField: "id",
    //   labelField: "name",
    // },
    // {
    //   key: "cxo",
    //   trigger: useLazyGetClassSituationSelectionCXOQuery,
    //   placeholder: "Select CXO",
    // },
  ]

  // Initialize state for options, pages, and hasMore
  const [options, setOptions] = useState(
    Object.fromEntries(
      selectConfig.map(({ key, placeholder }) => [
        key,
        [{ value: "", label: placeholder }],
      ])
    )
  )

  const [pages, setPages] = useState(
    Object.fromEntries(selectConfig.map(({ key }) => [key, 0]))
  )

  const [hasMore, setHasMore] = useState(
    Object.fromEntries(selectConfig.map(({ key }) => [key, true]))
  )

  const [teacherKeyword, setTeacherKeyword] = useState("")

  // Initialize RTK Query triggers
  const triggers = Object.fromEntries(
    selectConfig.map(({ key, trigger }) => [key, trigger()[0]])
  )

  // Sync tempFilters with initialFilters when menu opens
  useEffect(() => {
    if (anchorEl) {
      setTempFilters(initialFilters)
    }
  }, [anchorEl, initialFilters])

  const handleFilterChange = (key, value) => {
    setTempFilters((prev) => ({ ...prev, [key]: value }))
  }

  const applyFilters = () => {
    onSubmit(tempFilters)
    setAnchorEl(null)
  }

  const clearFilters = () => {
    const clearedFilters = {
      ...Object.fromEntries(selectConfig.map(({ key }) => [key, null])),
      fromDate: null,
      toDate: null,
    }
    setTempFilters(clearedFilters)
    onSubmit(clearedFilters)
    setAnchorEl(null)
  }

  const fetchOptions = async (key, page = 0, keyword = "") => {
    if (!hasMore[key]) return

    try {
      const config = selectConfig.find((c) => c.key === key)
      const resp = await triggers[key]({
        page,
        ...(config.isSearchable && { keyword }),
      })

      if (resp?.data) {
        const { data, currentPage, totalPage } = resp.data
        if (data) {
          setOptions((prev) => ({
            ...prev,
            [key]:
              page === 0
                ? [
                    { value: "", label: config.placeholder },
                    ...data.map((item) => ({
                      value: config.valueField ? item[config.valueField] : item,
                      label: config.labelField ? item[config.labelField] : item,
                    })),
                  ]
                : [
                    ...prev[key],
                    ...data.map((item) => ({
                      value: config.valueField ? item[config.valueField] : item,
                      label: config.labelField ? item[config.labelField] : item,
                    })),
                  ],
          }))

          setHasMore((prev) => ({
            ...prev,
            [key]: totalPage > 0 && currentPage < totalPage,
          }))
        }
      } else {
        setHasMore((prev) => ({ ...prev, [key]: false }))
      }
    } catch (error) {
      console.error(`Error fetching ${key}:`, error)
      setHasMore((prev) => ({ ...prev, [key]: false }))
    }
  }

  const handleTeacherSearch = (keyword) => {
    setTeacherKeyword(keyword)
    setPages((prev) => ({ ...prev, teacherName: 0 }))
    setHasMore((prev) => ({ ...prev, teacherName: true }))
    fetchOptions("teacherName", 0, keyword)
  }

  const handleMenuScrollToBottom = (key) => () => {
    if (!hasMore[key]) return

    setPages((prev) => {
      const newPage = prev[key] + 1
      fetchOptions(key, newPage, key === "teacherName" ? teacherKeyword : "")
      return { ...prev, [key]: newPage }
    })
  }

  const customStyles = {
    menu: (provided) => ({
      ...provided,
      maxHeight: 190,
      overflowY: "auto",
    }),
  }

  return (
    <Popover
      className={className}
      anchorEl={anchorEl}
      open={Boolean(anchorEl)}
      onClose={() => setAnchorEl(null)}
      anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      transformOrigin={{ vertical: "top", horizontal: "right" }}
    >
      <Grid container spacing={2} className={`${className}-wrap-select`}>
        <Grid item xs={12} className={`${className}-title`}>
          <Typography variant="h3">Filter</Typography>
        </Grid>
        <Grid item xs={6}>
          <MDInput
            fullWidth
            type="date"
            label="From date"
            InputLabelProps={{ shrink: true }}
            value={tempFilters.fromDate || ""}
            inputProps={{ max: tempFilters.toDate || undefined }}
            onChange={(e) => handleFilterChange("fromDate", e.target.value)}
          />
        </Grid>
        <Grid item xs={6}>
          <MDInput
            fullWidth
            type="date"
            label="To date"
            InputLabelProps={{ shrink: true }}
            value={tempFilters.toDate || ""}
            inputProps={{ min: tempFilters.fromDate || undefined }}
            onChange={(e) => handleFilterChange("toDate", e.target.value)}
          />
        </Grid>
        {selectConfig.map(({ key, placeholder, isSearchable }) => (
          <Grid item xs={6} key={key}>
            <MDSelect
              options={options[key]}
              value={tempFilters[key] || ""}
              onChange={(value) => handleFilterChange(key, value)}
              onMenuOpen={() =>
                fetchOptions(
                  key,
                  0,
                  key === "teacherName" ? teacherKeyword : ""
                )
              }
              onMenuScrollToBottom={handleMenuScrollToBottom(key)}
              {...(isSearchable && {
                onInputChange: handleTeacherSearch,
                isSearchable: true,
              })}
              styles={customStyles}
              placeholder={placeholder}
              maxMenuHeight={190}
              menuPlacement={
                ["classStatusId", "cxo"].includes(key) ? "top" : "bottom"
              }
            />
          </Grid>
        ))}
      </Grid>
      <MDBox
        sx={{
          display: "flex",
          justifyContent: "space-between",
          width: "100%",
          gap: "8px",
          padding: "8px",
        }}
      >
        <Button
          className={`${className}-btn ${className}-btn-clear`}
          onClick={clearFilters}
          color="error"
          sx={{ width: "100%" }}
        >
          Clear all
        </Button>
        <Button
          className={`${className}-btn ${className}-btn-apply`}
          onClick={() => {
            triggerIfNew()
            applyFilters()
          }}
          color="primary"
          variant="contained"
          disabled={
            Boolean(tempFilters.fromDate && tempFilters.toDate) &&
            tempFilters.fromDate > tempFilters.toDate
          }
          sx={{ width: "100%" }}
        >
          Filter
        </Button>
      </MDBox>
    </Popover>
  )
}

export default Filters
